import { useCallback, useState } from 'react'

import { Space, Button, Tooltip, Input, message } from 'antd'
import { useIntl } from 'react-intl'

import { CopyOutlined, EmailOutlined, SmsOutlined, EyeOpenSolid, EyeSlashSolid } from '../../icons'
import { useSendCommunicationMutation } from '../../api/enrollement'
import styled from 'styled-components'

export interface PassphraseViewerProps {
  passphrase: string
  emailNotification?: boolean
  smsNotification?: boolean
}

const CopyBlueOutlined = styled(CopyOutlined)`
  path {
    stroke: #3C84E0;
  }
`

const EmailBlueOutlined = styled(EmailOutlined)<{disabled?: boolean}>`
  path {
    fill: ${props => props.disabled ? '#ACAEB0' : '#689ee3'};
  }
  scale: 1.2;
`

const SmsBlueOutlined = styled(SmsOutlined)<{disabled?: boolean}>`
  path {
    fill: ${props => props.disabled ? '#ACAEB0' : '#689ee3'};
  }
  scale: 1.2;
`

export function PassphraseViewer (props: PassphraseViewerProps) {
  const { passphrase, emailNotification = false, smsNotification = false } = props
  const { $t } = useIntl()
  const [sendCommunication, {isLoading: isCommunicationsLoading}] = useSendCommunicationMutation()
  const copyButtonTooltipDefaultText = $t({ defaultMessage: 'Copy Passphrase' })
  const copyButtonTooltipCopiedText = $t({ defaultMessage: 'Passphrase Copied' })
  const [copyButtonTooltip, setCopyTooltip] = useState(copyButtonTooltipDefaultText)

  const passwordIconRender = (visible: boolean) =>
    visible ? <EyeSlashSolid size='sm' data-testid='EyeSlashSolid' />
      : <EyeOpenSolid size='sm' data-testid='EyeOpenSolid' />

  const handleSendEmail = useCallback(() => {
    sendCommunication({ type: 'EMAIL' }).unwrap()
      .then(() => {
        message.success($t({ defaultMessage: 'Sent passphrase by Email.' }))
      })
      .catch(() => {
        message.error($t({ defaultMessage: 'Failed to send passphrase by Email.' }))
      })
  }, [sendCommunication])

  const handleSendSms = useCallback(() => {
    sendCommunication({ type: 'SMS' }).unwrap()
      .then(() => {
        message.success($t({ defaultMessage: 'Sent passphrase by SMS.' }))
      })
      .catch(() => {
        message.error($t({ defaultMessage: 'Failed to send passphrase by SMS.' }))
      })
  }, [sendCommunication])

  return (
    <Space direction='horizontal' size={2} onClick={(e) => { e.stopPropagation() }}>
      <Input.Password
        readOnly
        style={{ paddingLeft: '8px' }}
        value={passphrase}
        iconRender={passwordIconRender}
      />
      <Tooltip title={copyButtonTooltip}>
        <Button 
          style={{border: 'none', boxShadow: 'none'}}
          data-testid='copyText'
          icon={<CopyBlueOutlined size='sm' />}
          onMouseOut={() => setCopyTooltip(copyButtonTooltipDefaultText)}
          onClick={() => {
            navigator.clipboard.writeText(passphrase)
            setCopyTooltip(copyButtonTooltipCopiedText)
          }}
        />
      </Tooltip>
      {emailNotification && (
        <Tooltip title={$t({ defaultMessage: 'Send by Email' })}>
          <Button
            style={{border: 'none', boxShadow: 'none', background: 'none'}}
            data-testid='sendEmail'
            disabled={isCommunicationsLoading}
            icon={<EmailBlueOutlined disabled={isCommunicationsLoading} size='sm' />}
            onClick={handleSendEmail}
          />
        </Tooltip>
      )}
      {smsNotification && (
        <Tooltip title={$t({ defaultMessage: 'Send by SMS' })}>
          <Button
            style={{border: 'none', boxShadow: 'none', background: 'none'}}
            data-testid='sendSms'
            icon={<SmsBlueOutlined disabled={isCommunicationsLoading} size='sm' />}
            disabled={isCommunicationsLoading}
            onClick={handleSendSms}
          />
        </Tooltip>
      )}
    </Space>
  )
}
